import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { SlidersHorizontal, Sparkles, Crown, Check, ArrowRight } from 'lucide-react';
import { CATEGORIES, QUEENS_CHOICE, IMPERIAL_LUXE, SILK_DYNASTY, getTestimonials } from '../mock';
import { fetchProducts } from '../lib/api';
import PhotoTestimonials from '../components/PhotoTestimonials';
import ProductCard from '../components/ProductCard';
import Reveal from '../components/Reveal';
import Magnetic from '../components/motion/Magnetic';
import { SkeletonProductGrid } from '../components/motion/Skeleton';
import bundlesBanner from '../assets/wigs_banner.webp';
import imperialLuxeBanner from '../assets/imperial_luxe_banner.webp';
import silkDynastyBanner from '../assets/silk_dynasty_banner.webp';
import signatureReignBanner from '../assets/signature_reign_banner.webp';
import queensChoiceBanner from '../assets/queens_choice_banner.webp';

// Per-category promo banner + two-collection layout config
const PAGE_CONFIG = {
  wigs: {
    collections: [
      { key: 'signature-reign', title: 'Signature Reign Collection', subtitle: 'Bold. Luxurious. Unapologetically You.', banner: signatureReignBanner, fullBanner: true },
      { key: 'queens-choice', title: "Queen's Choice Collection", subtitle: 'The Bob Collection — Effortless. Flawless. Iconic.', banner: queensChoiceBanner, fullBanner: true },
    ],
  },
  bundles: {
    collections: [
      { key: 'imperial-luxe', title: 'Imperial Luxe Collection', subtitle: 'Super Drawn Luxury Hair', banner: imperialLuxeBanner, fullBanner: true },
      { key: 'silk-dynasty', title: 'Silk Dynasty Collection', subtitle: 'Minimally Processed Virgin Hair', banner: silkDynastyBanner, fullBanner: true },
    ],
  },
};

const SilkDynastyDescription = () => (
  <div className="max-w-3xl mx-auto text-center mb-12 -mt-4">
    <p className="font-body text-[#4a463d] leading-loose mb-4">
      Introducing the Silk Dynasty Collection from Nikki's Next Level Hair &amp; Beauty — created for women who love volume, glamour, and statement-making beauty.
    </p>
    <p className="font-body text-[#4a463d] leading-loose mb-4">
      Crafted from premium-quality human hair, Silk Dynasty delivers rich fullness, luxurious body, and exceptional movement. Designed to create stunning, head-turning styles, this collection provides the perfect balance of softness, durability, and versatility for queens who demand nothing less than excellence.
    </p>
    <p className="font-body text-[#4a463d] leading-loose mb-8">
      Whether you're creating glamorous waves, voluminous curls, or a flawless luxury install, Silk Dynasty offers the premium quality and confidence needed to elevate every look.
    </p>

    <div className="grid sm:grid-cols-3 gap-8 text-left bg-[#f2ece0] border border-[#e6ddcd] p-8">
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Collection Features</h4>
        <ul className="space-y-2">
          {['Minimally Processed Virgin Hair', 'Premium-Quality Human Hair', 'Rich Fullness & Volume', 'Soft, Luxurious Texture', 'Minimal Shedding & Tangle Resistance', 'Long-Lasting Durability', 'Natural Shine & Movement', 'Heat Styling Friendly', 'Available in Lengths 8"–30"', 'Multiple Colors & Textures'].map((f) => (
            <li key={f} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><Crown size={14} className="mt-0.5 text-[#c6a15b] shrink-0" /> {f}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Available Textures</h4>
        <ul className="space-y-2">
          {['Straight', 'Body Wave', 'Deep Wave', 'Loose Deep Wave', 'Water Wave', 'Natural Wave', 'Loose Wave', 'Kinky Curly'].map((t) => (
            <li key={t} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><span className="text-[#c6a15b] leading-none">•</span> {t}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Perfect For</h4>
        <ul className="space-y-2">
          {['Glamorous Everyday Wear', 'Luxury Installs', 'Custom Wig Construction', 'Sew-Ins & Protective Styles', 'Special Events & Photoshoots', 'Full, Voluminous Styles'].map((p) => (
            <li key={p} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><Check size={14} className="mt-0.5 text-[#a8823c] shrink-0" /> {p}</li>
          ))}
        </ul>
      </div>
    </div>

    <p className="font-body text-[#4a463d] leading-loose mt-8">
      Designed for women who embrace confidence, elegance, and undeniable beauty, Silk Dynasty transforms every style into a royal experience.
    </p>
    <p className="font-display text-2xl text-[#1a1a1a] italic mt-4">“Where Every Queen Builds Her Empire.”</p>
  </div>
);

const ImperialLuxeDescription = () => (
  <div className="max-w-3xl mx-auto text-center mb-12 -mt-4">
    <p className="overline text-[#a8823c] mb-5 tracking-[0.3em]">Luxury. Elegance. Perfection.</p>
    <p className="font-body text-[#4a463d] leading-loose mb-4">
      Experience the beauty of true luxury with the Imperial Luxe Collection from Nikki's Next Level Hair &amp; Beauty. Crafted from premium-quality human hair, Imperial Luxe is designed for women who demand sophistication, versatility, and flawless results.
    </p>
    <p className="font-body text-[#4a463d] leading-loose mb-4">
      Featuring a silky-soft texture, natural movement, and exceptional durability, Imperial Luxe delivers a polished, salon-quality look that blends seamlessly while maintaining its luxurious appearance wear after wear.
    </p>
    <p className="font-body text-[#4a463d] leading-loose mb-8">
      Whether you prefer sleek straight styles, glamorous body waves, or soft flowing curls, Imperial Luxe provides the perfect foundation for effortless beauty and confidence.
    </p>

    <div className="grid sm:grid-cols-3 gap-8 text-left bg-[#f2ece0] border border-[#e6ddcd] p-8">
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Collection Features</h4>
        <ul className="space-y-2">
          {['Super Drawn Luxury Hair', 'Premium-Quality Human Hair', 'Soft, Silky Texture', 'Minimal Shedding & Tangle Resistance', 'Long-Lasting Durability', 'Natural Shine & Movement', 'Heat Styling Friendly', 'Available in Lengths 8"–30"', 'Multiple Colors & Textures'].map((f) => (
            <li key={f} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><Crown size={14} className="mt-0.5 text-[#c6a15b] shrink-0" /> {f}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Available Textures</h4>
        <ul className="space-y-2">
          {['Straight', 'Body Wave', 'Deep Wave', 'Loose Deep Wave', 'Water Wave', 'Natural Wave', 'Loose Wave', 'Kinky Curly'].map((t) => (
            <li key={t} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><span className="text-[#c6a15b] leading-none">•</span> {t}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="overline text-[#a8823c] mb-4">Perfect For</h4>
        <ul className="space-y-2">
          {['Everyday Luxury', 'Professional Styling', 'Special Events', 'Full Installs', 'Custom Wig Construction', 'Sew-Ins & Protective Styles'].map((p) => (
            <li key={p} className="flex items-start gap-2 font-body text-sm text-[#4a463d]"><Check size={14} className="mt-0.5 text-[#a8823c] shrink-0" /> {p}</li>
          ))}
        </ul>
      </div>
    </div>

    <p className="font-body text-[#4a463d] leading-loose mt-8">
      Imperial Luxe is more than hair — it's a statement of confidence, beauty, and timeless elegance.
    </p>
    <p className="font-display text-2xl text-[#1a1a1a] italic mt-4">“Where Luxury Meets Limitless Beauty.”</p>
  </div>
);

const PlaceholderCard = ({ prefix, texture }) => (
  <div className="group">
    <div className="relative overflow-hidden aspect-[3/4] bg-[#141311] flex flex-col items-center justify-center border border-[#c6a15b]/20">
      <Sparkles size={30} className="text-[#c6a15b] mb-4" />
      <p className="font-display text-2xl text-[#f7f3ec] px-4 text-center leading-tight">{texture}</p>
      <span className="mt-4 overline text-[0.58rem] text-[#e7d3a1] border border-[#c6a15b]/40 px-3 py-1.5">Coming Soon</span>
    </div>
    <div className="pt-4 text-center">
      <h3 className="font-display text-lg text-[#1a1a1a] leading-snug px-2">{prefix} – {texture}</h3>
      <p className="font-body text-sm text-[#6b6459] mt-1 tracking-wide">Coming Soon</p>
    </div>
  </div>
);

const SHADE_FILTERS = [
  { key: 'black', label: 'Black', swatch: '#191919', re: /black/i },
  { key: 'brown', label: 'Brown', swatch: '#4a2c1a', re: /brown|chestnut/i },
  { key: 'auburn', label: 'Auburn', swatch: '#7a3b1a', re: /auburn/i },
  { key: 'burgundy', label: 'Wine / Burgundy', swatch: '#5b0f2a', re: /wine|burgundy/i },
  { key: 'ginger', label: 'Ginger / Copper', swatch: '#c25a1e', re: /ginger|copper/i },
  { key: 'blonde', label: 'Blonde', swatch: '#e6cd94', re: /blonde/i },
  { key: 'red', label: 'Red', swatch: '#b0201f', re: /\bred\b/i },
];

const Shop = () => {
  const { category } = useParams();
  const location = useLocation();
  const [sort, setSort] = useState('featured');
  const [shade, setShade] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('shade');
    return SHADE_FILTERS.some((s) => s.key === q) ? q : '';
  });
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('shade');
    if (q && SHADE_FILTERS.some((s) => s.key === q)) setShade(q);
  }, [location.search]);
  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [category, location.hash]);

  const activeCat = CATEGORIES.find((c) => c.slug === category);
  const pageConfig = PAGE_CONFIG[category];

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetchProducts(category)
      .then(setAllProducts)
      .catch(() => setAllProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const products = useMemo(() => {
    let list = [...allProducts];
    if (shade) {
      const f = SHADE_FILTERS.find((s) => s.key === shade);
      if (f) list = list.filter((p) => (p.colors || []).some((c) => f.re.test(c)));
    }
    if (sort === 'price-low') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price-high') list = [...list].sort((a, b) => b.price - a.price);
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    // keep coming-soon items at the end (stable)
    list = [...list].sort((a, b) => (a.coming_soon ? 1 : 0) - (b.coming_soon ? 1 : 0));
    return list;
  }, [allProducts, sort, shade]);

  return (
    <div>
      {/* Banner */}
      <section className="relative bg-[#0b0b0b] py-20 sm:py-24 text-center overflow-hidden">
        {activeCat && <img src={activeCat.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b]/70 to-[#0b0b0b]" />
        <div className="relative">
          <p className="overline text-gold-gradient mb-3">{activeCat ? 'Collection' : 'The Full Range'}</p>
          <h1 className="font-display text-5xl sm:text-7xl text-[#f7f3ec] underline-grow">{activeCat ? activeCat.name : 'Shop All'}</h1>
          {activeCat && <p className="font-body text-[#cfc7b8] mt-3">{activeCat.tagline}</p>}
        </div>
      </section>

      {/* Collection promo banner (bundles overview only) */}
      {category === 'bundles' && (
        <div className="block max-w-[1400px] mx-auto px-4 sm:px-6 pt-8">
          <div className="overflow-hidden rounded-md shadow-lg">
            <img src={bundlesBanner} alt={`${activeCat?.name} collections`} className="w-full h-auto object-cover" />
          </div>
        </div>
      )}

      {/* Category pills + sort */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-5 border-b border-[#e6ddcd]">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Link to="/shop" className={`px-5 py-2 overline text-[0.62rem] border transition-colors ${!category ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#6b6459] hover:border-[#c6a15b]'}`}>All</Link>
          {CATEGORIES.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} className={`px-5 py-2 overline text-[0.62rem] border transition-colors ${category === c.slug ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#6b6459] hover:border-[#c6a15b]'}`}>{c.name}</Link>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[#6b6459]">
          <SlidersHorizontal size={15} />
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-transparent font-body text-sm outline-none cursor-pointer text-[#1a1a1a]">
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Top Rated</option>
          </select>
        </div>
      </div>

      {/* Shade filter — find a color across products */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center gap-2.5 border-b border-[#e6ddcd]" data-testid="shade-filter">
        <span className="overline text-[0.6rem] text-[#a8823c] mr-1">Shop by Color</span>
        <button onClick={() => setShade('')} data-testid="shade-all"
          className={`px-4 py-1.5 overline text-[0.58rem] border transition-colors ${!shade ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#6b6459] hover:border-[#c6a15b]'}`}>All Shades</button>
        {SHADE_FILTERS.map((s) => (
          <button key={s.key} onClick={() => setShade(shade === s.key ? '' : s.key)} data-testid={`shade-${s.key}`}
            className={`flex items-center gap-2 px-3.5 py-1.5 overline text-[0.58rem] border transition-colors ${shade === s.key ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#6b6459] hover:border-[#c6a15b]'}`}>
            <span className="w-3 h-3 rounded-full border border-black/20 shrink-0" style={{ background: s.swatch }} />
            {s.label}
          </button>
        ))}
      </div>
      {pageConfig ? (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14">
          {loading ? (
            <SkeletonProductGrid count={8} />
          ) : pageConfig.collections.map((col, ci) => {
            const colProducts = products
              .filter((p) => p.collection === col.key)
              .sort((a, b) => (a.coming_soon ? 1 : 0) - (b.coming_soon ? 1 : 0));
            const placeholders = col.key === 'queens-choice' ? QUEENS_CHOICE : col.key === 'imperial-luxe' ? IMPERIAL_LUXE : col.key === 'silk-dynasty' ? SILK_DYNASTY : [];
            if (!col.placeholderPrefix && colProducts.length === 0) return null;
            return (
              <div key={col.key} className={ci > 0 ? 'mt-24' : ''}>
                {col.banner ? (
                  col.fullBanner ? (
                    <div className="mb-8" data-testid={`collection-banner-${col.key}`}>
                      <div className="rounded-md overflow-hidden shadow-xl ring-1 ring-[#c6a15b]/20">
                        <img src={col.banner} alt={col.title} className="w-full h-auto block" />
                      </div>
                    </div>
                  ) : (
                  <div className="relative overflow-hidden rounded-md mb-10 aspect-[16/8] sm:aspect-[16/6]" data-testid={`collection-banner-${col.key}`}>
                    <img src={col.banner} alt={col.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b]/85 via-[#0b0b0b]/45 to-transparent" />
                    <div className="relative h-full flex flex-col justify-center pl-6 sm:pl-14 max-w-xl">
                      <p className="font-display italic text-2xl sm:text-4xl text-gold-gradient mb-2">{col.subtitle}</p>
                      <h2 className="font-display text-3xl sm:text-6xl text-[#f7f3ec]">{col.title}</h2>
                      <div className="gold-line w-20 mt-5" />
                      <Magnetic className="self-start mt-6">
                        <button
                          onClick={() => document.getElementById(`grid-${col.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          data-testid={`shop-collection-${col.key}`}
                          className="btn-gold px-7 py-3">
                          <span className="flex items-center gap-2">Shop the Collection <ArrowRight size={15} /></span>
                        </button>
                      </Magnetic>
                    </div>
                  </div>
                  )
                ) : (
                  <div className="text-center mb-10">
                    <p className="font-display italic text-2xl sm:text-3xl text-gold-gradient mb-3">{col.subtitle}</p>
                    <h2 className="font-display text-4xl sm:text-5xl text-[#1a1a1a]">{col.title}</h2>
                    <div className="gold-line w-20 mx-auto mt-5" />
                  </div>
                )}
                {!col.fullBanner && col.key === 'imperial-luxe' && <ImperialLuxeDescription />}
                {!col.fullBanner && col.key === 'silk-dynasty' && <SilkDynastyDescription />}
                <div id={`grid-${col.key}`} className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 scroll-mt-24">
                  {col.placeholderPrefix
                    ? placeholders.map((p) => <PlaceholderCard key={p.id} prefix={col.placeholderPrefix} texture={p.texture} />)
                    : colProducts.map((p, i) => (
                        <Reveal key={p.id} delay={(i % 4) * 0.08} y={30}>
                          <ProductCard product={p} index={i} />
                        </Reveal>
                      ))}
                </div>
              </div>
            );
          })}
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14">
          {loading ? (
            <SkeletonProductGrid count={8} />
          ) : (
            <>
              <p className="font-body text-sm text-[#6b6459] mb-8">{products.length} products</p>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                {products.map((p, i) => (
                  <Reveal key={p.id} delay={(i % 4) * 0.08} y={30}>
                    <ProductCard product={p} index={i} comingSoonClickable />
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </section>
      )}
      <PhotoTestimonials items={getTestimonials(category)}
        subtitle="Real Reviews"
        title="Loved by Our Queens"
        dark={false}
        variant="cards" />
    </div>
  );
};

export default Shop;
